import { supabase } from '../lib/supabase';
import Papa from 'papaparse';
import { validationUtils } from '../utils/validation';
import { errorHandler } from '../utils/errorHandler';

/**
 * Bulk Upload Service - Handles bulk student data upload
 */

export const bulkUploadService = {
  /**
   * Parse CSV file and return structured data
   */
  parseCSVFile(file) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header?.trim()?.toLowerCase()?.replace(/\s+/g, '_'),
        complete: (results) => {
          resolve(results);
        },
        error: (error) => {
          reject(new Error(`CSV parsing failed: ${error?.message}`));
        }
      });
    });
  },

  /**
   * Validate CSV data structure and content
   */
  validateCSVData(data) {
    const errors = [];
    const validRows = [];
    
    // Required fields
    const requiredFields = ['email', 'full_name', 'mobile', 'city', 'state', 'course_interest'];
    
    // Check if CSV has required headers
    if (data?.length === 0) {
      return { valid: false, errors: ['CSV file is empty'], validRows: [] };
    }
    
    const headers = Object.keys(data?.[0] || {});
    const missingHeaders = requiredFields?.filter(field => !headers?.includes(field));
    
    if (missingHeaders?.length > 0) {
      return {
        valid: false,
        errors: [`Missing required columns: ${missingHeaders?.join(', ')}`],
        validRows: []
      };
    }
    
    // Validate each row
    data?.forEach((row, index) => {
      const rowErrors = [];
      const rowNumber = index + 2; // +2 because index starts at 0 and row 1 is header
      
      // Validate email
      if (!row?.email || !validationUtils?.isValidEmail(row?.email)) {
        rowErrors?.push(`Row ${rowNumber}: Invalid email`);
      }
      
      // Validate mobile
      if (!row?.mobile || !validationUtils?.isValidPhone(row?.mobile)) {
        rowErrors?.push(`Row ${rowNumber}: Invalid mobile number`);
      }
      
      // Validate required fields
      requiredFields?.forEach(field => {
        if (!row?.[field] || row?.[field]?.trim() === '') {
          rowErrors?.push(`Row ${rowNumber}: Missing ${field?.replace('_', ' ')}`);
        }
      });
      
      if (rowErrors?.length > 0) {
        errors?.push(...rowErrors);
      } else {
        validRows?.push({
          ...row,
          rowNumber
        });
      }
    });
    
    return {
      valid: errors?.length === 0,
      errors,
      validRows,
      totalRows: data?.length,
      validCount: validRows?.length,
      errorCount: errors?.length
    };
  },

  /**
   * Generate secure random password
   */
  generatePassword() {
    const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lowercase = 'abcdefghjkmnpqrstuvwxyz';
    const numbers = '23456789';
    const special = '@#$%&*';
    
    let password = '';
    
    // Ensure at least one of each type
    password += uppercase?.charAt(Math.floor(Math.random() * uppercase?.length));
    password += lowercase?.charAt(Math.floor(Math.random() * lowercase?.length));
    password += numbers?.charAt(Math.floor(Math.random() * numbers?.length));
    password += special?.charAt(Math.floor(Math.random() * special?.length));
    
    // Fill remaining with random characters
    const allChars = uppercase + lowercase + numbers + special;
    for (let i = 4; i < 10; i++) {
      password += allChars?.charAt(Math.floor(Math.random() * allChars?.length));
    }
    
    // Shuffle password
    return password?.split('')?.sort(() => Math.random() - 0.5)?.join('');
  },

  /**
   * Generate unique student ID
   */
  generateStudentId() {
    const timestamp = Date.now()?.toString()?.slice(-6);
    const random = Math.floor(Math.random() * 1000)?.toString()?.padStart(3, '0');
    return `STU${timestamp}${random}`;
  },

  /**
   * Create single student account
   */
  async createStudentAccount(studentData) {
    return errorHandler?.handleAsync(async () => {
      let password = this.generatePassword();
      const studentId = this.generateStudentId();
      
      // Sanitize inputs
      const sanitizedData = {
        email: validationUtils?.sanitizeString(studentData?.email)?.toLowerCase(),
        fullName: validationUtils?.sanitizeString(studentData?.full_name),
        mobile: validationUtils?.sanitizeString(studentData?.mobile)?.replace(/[\s()-]/g, ''),
        city: validationUtils?.sanitizeString(studentData?.city),
        state: validationUtils?.sanitizeString(studentData?.state),
        courseInterest: validationUtils?.sanitizeString(studentData?.course_interest),
        examStatus: validationUtils?.sanitizeString(studentData?.exam_status || 'Not Specified'),
        budgetRange: validationUtils?.sanitizeString(studentData?.budget_range || 'Not Specified'),
        studyMode: validationUtils?.sanitizeString(studentData?.study_mode || 'Not Specified')
      };
      
      // Create auth user
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: sanitizedData?.email,
        password,
        options: {
          emailRedirectTo: `${window.location?.origin}/auth/callback`,
          data: {
            role: 'student',
            name: sanitizedData?.fullName
          }
        }
      });
      
      if (authError) {
        // Check if user already exists
        if (authError?.message?.includes('already registered')) {
          throw new Error(`Email ${sanitizedData?.email} already exists`);
        }
        throw authError;
      }
      
      // Insert into users table
      const { error: userError } = await supabase?.from('users')?.insert({
        id: authData?.user?.id,
        email: sanitizedData?.email,
        phone: sanitizedData?.mobile,
        role: 'student',
        name: sanitizedData?.fullName,
        city: sanitizedData?.city,
        state: sanitizedData?.state,
        is_verified: true, // Auto-verify bulk uploaded students
        password_hash: 'supabase_auth_managed'
      });
      
      if (userError) throw userError;
      
      // Insert into student_profiles table
      const { error: profileError } = await supabase?.from('student_profiles')?.insert({
        user_id: authData?.user?.id,
        course_interest: sanitizedData?.courseInterest,
        exam_status: sanitizedData?.examStatus,
        budget_range: sanitizedData?.budgetRange,
        study_mode: sanitizedData?.studyMode,
        consent_given: true
      });
      
      if (profileError) throw profileError;
      
      return {
        success: true,
        studentId,
        email: sanitizedData?.email,
        password,
        name: sanitizedData?.fullName,
        userId: authData?.user?.id
      };
    }, 'createStudentAccount');
  },

  /**
   * Bulk create student accounts
   */
  async bulkCreateStudents(validRows, onProgress) {
    const results = {
      successful: [],
      failed: []
    };
    
    const total = validRows?.length;
    
    for (let i = 0; i < validRows?.length; i++) {
      const row = validRows?.[i];
      
      try {
        const result = await this.createStudentAccount(row);
        results?.successful?.push({
          ...result,
          rowNumber: row?.rowNumber
        });
      } catch (error) {
        results?.failed?.push({
          rowNumber: row?.rowNumber,
          email: row?.email,
          name: row?.full_name,
          error: error?.message || 'Unknown error'
        });
      }
      
      // Report progress
      if (onProgress) {
        onProgress({
          current: i + 1,
          total,
          percentage: Math.round(((i + 1) / total) * 100)
        });
      }
      
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return results;
  },

  /**
   * Export results to CSV
   */
  exportResultsToCSV(results) {
    const csvData = results?.successful?.map(item => ({
      'Student ID': item?.studentId,
      'Name': item?.name,
      'Email': item?.email,
      'Password': item?.password,
      'Status': 'Success'
    }));
    
    results?.failed?.forEach(item => {
      csvData?.push({
        'Student ID': 'N/A',
        'Name': item?.name,
        'Email': item?.email,
        'Password': 'N/A',
        'Status': `Failed: ${item?.error}`
      });
    });
    
    const csv = Papa?.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link?.setAttribute('href', url);
    link?.setAttribute('download', `bulk_upload_results_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  },

  /**
   * Download CSV template
   */
  downloadTemplate() {
    const template = [
      {
        email: 'student@example.com',
        full_name: 'John Doe',
        mobile: '9876543210',
        city: 'Mumbai',
        state: 'Maharashtra',
        course_interest: 'Engineering',
        exam_status: 'JEE Appeared',
        budget_range: '5-10 Lakhs',
        study_mode: 'Full-time'
      }
    ];
    
    const csv = Papa?.unparse(template);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link?.setAttribute('href', url);
    link?.setAttribute('download', 'student_upload_template.csv');
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  }
};

export default bulkUploadService;
